import React, { Component } from 'react';
import Cursor from '../../../../../../../../../../../css/ppt-editor-cursor-global.less';
import _has from 'lodash/has';
import * as Graphic from '../../../../../../../../../../../slide/infrastructure/module/independent/graphic';
import { createGraphicsParser, ICustomPath } from '@tencent/alloy-graphics';
import { ShapeType } from '@src/slide/infrastructure/module/dependent/shape-info/graphic-type';

export interface GraphicContentEditorProps {
    model: {
        border?: {
            sketchStyle?: {
                bgShape?: ShapeType;
            };
        };
        bgShape?: ShapeType;
        width: number;
        height: number;
        pathList?: Array<ICustomPath>;
        controlValues?: number[];
        rotate?: number;
        flipH?: number;
        flipV?: number;
    };
    scale: number;
    onControlPointMouseDown?: () => void;
    onControlPointMoving?: (controlValues: number[], textRect: any) => void;
    onControlValueChange?: (controlValues: number[]) => void;
}

interface ControlPoint {
    x: number;
    y: number;
}

export default class GraphicContentEditor<T extends GraphicContentEditorProps, P = {}> extends Component<T, P> {
    protected pathParser: any;
    protected startPos: { x: number; y: number; };
    protected changeControlPointIndex: number;
    protected leftInClient: number;
    protected topInClient: number;
    protected controlPoints: ControlPoint[];

    public constructor(props: T, context) {
        super(props, context);
        this.initPathParser(props);
    }

    public UNSAFE_componentWillReceiveProps(nextProps: T) {
        this.initPathParser(nextProps);
    }

    public componentWillUnmount() {
        if (this.pathParser) {
            this.pathParser = null;
        }
    }

    public render(): JSX.Element | null {
        return (
            <React.Fragment>
                {
                    this.renderControlPoints()
                }
            </React.Fragment>
        );
    }

    protected renderControlPoints = () => {
        this.controlPoints = this.pathParser.getControlPoints();
        return this.controlPoints.map(this.renderControlPoint);
    };

    protected renderControlPoint = (point: ControlPoint, index: number) => {
        const diameter = 10 / this.props.scale;
        const path = `M${diameter / 2} ${0} L${diameter} ${diameter / 2} L${diameter / 2} ${diameter} L${0} ${diameter / 2} Z`;
        return (
            <svg
                key={index}
                xmlns="http://www.w3.org/2000/svg"
                version="1.1"
                style={{
                    width: diameter,
                    height: diameter,
                    left: point.x - (diameter / 2),
                    top: point.y - (diameter / 2),
                    position: 'absolute',
                    zIndex: 1000,
                }}
                filter={`drop-shadow(0 ${1 / this.props.scale} ${3 / this.props.scale} rgba(0,0,0,0.32))`}
            >
                <path
                    stroke={'#FFFFFF'}
                    strokeWidth={1 / this.props.scale}
                    fill={'#F5A623'}
                    d={path}
                    onMouseDown={this.handleControlPointMouseDown.bind(this, index)}
                    pointerEvents={'auto'}
                    className={Cursor['nwse-resize']}
                >
                </path>
            </svg>
        );
    };
    protected handleControlPointMouseMove = (e: MouseEvent | TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
        let currentEventPos = this.getRelativePosByEvent(e);
        if (_has(this.props.model, 'rotate') && this.props.model.rotate) {
            const centerX = this.leftInClient + (this.props.model.width / 2);
            const centerY = this.topInClient + (this.props.model.height / 2);
            currentEventPos = Graphic.afterRotate(currentEventPos, 360 - this.props.model.rotate, centerX, centerY);
        }
        const currentPosWithRect = {
            x: currentEventPos.x - this.leftInClient,
            y: currentEventPos.y - this.topInClient,
        };
        const controlValues = this.pathParser.getControlValuesWhenControlPointChange(
            this.changeControlPointIndex,
            currentPosWithRect,
        )?.slice?.();
        this.pathParser.updateControlValues(controlValues);

        if (this.props.onControlPointMoving) {
            const textRect = this.pathParser.getTextRect?.();
            this.props.onControlPointMoving(controlValues, textRect);
        }
    };

    protected handleControlPointMouseDown = (index: number, e: React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
        this.startPos = this.getRelativePosByEvent(e);
        this.changeControlPointIndex = index;
        let controlPoint = { x: this.controlPoints[index].x, y: this.controlPoints[index].y };
        if (_has(this.props.model, 'rotate') && this.props.model.rotate) {
            const { rotate, width, height } = this.props.model;
            controlPoint = Graphic.afterRotate(controlPoint, rotate, width / 2, height / 2);
        }
        this.leftInClient = this.startPos.x - controlPoint.x;
        this.topInClient = this.startPos.y - controlPoint.y;
        document.addEventListener('mousemove', this.handleControlPointMouseMove);
        document.addEventListener('mouseup', this.handleControlPointChangeEnd);
        document.addEventListener('touchmove', this.handleControlPointMouseMove, { passive: false });
        document.addEventListener('touchend', this.handleControlPointChangeEnd, { passive: false });
        if (this.props.onControlPointMouseDown) {
            this.props.onControlPointMouseDown();
        }
    };

    protected handleControlPointChangeEnd = (e: MouseEvent | TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
        document.removeEventListener('mousemove', this.handleControlPointMouseMove);
        document.removeEventListener('mouseup', this.handleControlPointChangeEnd);
        document.removeEventListener('touchmove', this.handleControlPointMouseMove);
        document.removeEventListener('touchend', this.handleControlPointChangeEnd);
        if (this.props.onControlValueChange) {
            this.props.onControlValueChange(this.pathParser.controlValues);
        }
    };

    protected getRelativePosByEvent = (e: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent) => {
        let relativePos = this.getEventCoordinates(e);
        if (this.props?.model?.flipH === 1) {
            relativePos = Graphic.afterFlipH(relativePos, 0);
        }
        if (this.props?.model?.flipV === 1) {
            relativePos = Graphic.afterFlipV(relativePos, 0);
        }
        if (_has(this.props, 'scale') && this.props.scale !== 0) {
            relativePos.x = relativePos.x / this.props.scale;
            relativePos.y = relativePos.y / this.props.scale;
        }
        return relativePos;
    };

    private getEventCoordinates = (e: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent) => (
        {
            x: 'touches' in e ? e.touches[0].clientX : e.clientX,
            y: 'touches' in e ? e.touches[0].clientY : e.clientY,
        }
    );

    private initPathParser(props: T) {
        const bgShape = props.model.border?.sketchStyle?.bgShape ?? props.model.bgShape ?? ShapeType.rect;
        const options = {
            type: bgShape,
            controlValues: props.model?.controlValues?.slice(),
            width: props.model.width,
            height: props.model.height,
            pathList: props.model.pathList,
        };
        if (!this.pathParser) {
            this.pathParser = createGraphicsParser(options);
        } else {
            this.pathParser.update(options);
        }
    }
}
